import React from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { compose, wrapDisplayName } from 'recompose';
import Moment from 'moment';
import markMessagesAsSeen from 'modules/components/web/chat/src/functions/markMessagesAsSeen';

import apiFetch from 'modules/api-fetch';
import Toast from 'modules/toast';
import { withFetch, withLoading } from 'modules/with-fetch';

import ChatHeaderClient from './ChatHeaderClient';
import ChatMessagesClient from './ChatMessagesClient';
import ChatTextBox from '../ChatTextBox';

// TODO - refactor this from including the trainer specific portions since it is only client facing now
class Chat extends React.Component {
  static propTypes = {
    myChatRooms: PropTypes.object.isRequired,
    myTrainer: PropTypes.object.isRequired,
    myClient: PropTypes.object.isRequired,
  };

  state = {
    resetImageForm: false,
    pendingOutgoingMessages: [],
  };

  componentDidMount() {
    this.interval = setInterval(() => {
      this.shortPoll();
    }, 15000);

    try {
      markMessagesAsSeen(
        this.props.myChatRooms.data[0].messages,
        this.props.myClient.aspNetUserId,
      );
    } catch (e) {
      console.devLog.info('Error marking message as seen:', e);
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.myChatRooms.data[0].messages.length !==
      prevProps.myChatRooms.data[0].messages.length
    ) {
      try {
        markMessagesAsSeen(
          this.props.myChatRooms.data[0].messages,
          this.props.myClient.aspNetUserId,
        );
      } catch (e) {
        console.devLog.info('Error marking message as soon');
      }
    }
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  setImageReset = () => this.setState({ resetImageForm: false });

  setupChatMessageForServer = (values) => ({
    ChatRoomId: this.getChatRoomForUser().id,
    body: values.text,
    imageUrl: values.imageUrl,
    videoUrl: values.videoUrl,
  });

  getUserData = (self = true) =>
    self ? this.props.myClient.data[0] : this.props.myTrainer.data[0];

  getChatRoomForUser = () => {
    const thisClientChatRoom = this.props.myChatRooms.data.filter(
      (chatRoom) => {
        const isTrainerInRoom = chatRoom.users.some(
          (user) => user.aspNetUserId === this.getUserData(false).aspNetUserId,
        );
        return isTrainerInRoom;
      },
    );
    return thisClientChatRoom[0];
  };

  shortPoll = () => {
    this.props.myChatRooms.refreshData(false);
  };

  scrollToBottom = () => {
    if (this.messagesEnd) {
      this.messagesEnd.scrollIntoView({ behavior: 'instant' });
    }
  };

  submitForm = async (values, formikProps) => {
    const chatMessageForServer = this.setupChatMessageForServer(values);

    let indexToRemove;
    // Add our outgoing messages to a temporary array that gets shown so the user gets feedback right away
    this.setState((prevState) => {
      indexToRemove = prevState.pendingOutgoingMessages.length;

      const pendingMessage = {
        ...chatMessageForServer,
        chatRoomId: chatMessageForServer.ChatRoomId, // It's a little pathetic we need to do this, and spend time troubleshooting it
        id: -1 - indexToRemove,
        sent: Moment().toISOString(),
        aspNetUserId: this.getUserData().aspNetUserId,
      };

      return {
        pendingOutgoingMessages: prevState.pendingOutgoingMessages.concat(
          pendingMessage,
        ),
      };
    });

    const config = {
      url: 'Client/CreateChatMessage',
      query: { isCMA: false },
      method: 'POST',
      body: chatMessageForServer,
    };

    formikProps.resetForm({ values: null });

    try {
      await apiFetch(config);
      await this.props.myChatRooms.refreshData(false);
    } catch (err) {
      Toast.error(`Error submitting message: ${err}`);
    } finally {
      // Since we waited until we both sent and refresh data, go ahead and remove the temporary messages
      // as the server will now have returned the complete new chat
      // Note this may be a possible race condition, as technically setState isn't guaranteed
      // to actually execute before the API returns, though we always expect it to
      this.setState((prevState) => ({
        pendingOutgoingMessages: prevState.pendingOutgoingMessages.filter(
          // eslint-disable-next-line no-underscore-dangle
          (message, index) => index !== indexToRemove,
        ),
      }));

      formikProps.setSubmitting(false);
      this.setState({
        resetImageForm: true,
      });
    }
  };

  render() {
    if (
      this.props.myChatRooms.data.length <= 0 ||
      this.props.myClient.data.length <= 0 ||
      this.props.myTrainer.data.length <= 0
    ) {
      return null;
    }

    const userRoom = this.getChatRoomForUser();
    if (!userRoom) {
      return <p>No chatroom found!</p>;
    }

    const { messages } = userRoom;
    const messagesWithPending = messages.concat(
      this.state.pendingOutgoingMessages,
    );

    const trainerData = this.props.myTrainer.data[0];
    const clientData = this.props.myClient.data[0];

    return (
      <div className="page chat ml-auto p-0 col-md-9 chat-client">
        <ChatHeaderClient clientData={clientData} trainerData={trainerData} />
        <div className="page--content chat-container">
          <div className="mx-auto p-0 col-10">
            <ChatMessagesClient
              messages={messagesWithPending}
              clientData={clientData}
              trainerData={trainerData}
            />
          </div>
          <Formik
            initialValues={{
              text: '',
              imageUrl: null,
              videoUrl: null,
            }}
            // validationSchema={}
            onSubmit={this.submitForm}
            render={(formikProps) => (
              <ChatTextBox
                className="ml-auto p-0 col-md-9"
                resetImageForm={this.state.resetImageForm}
                setImageReset={this.setImageReset}
                scrollToBottom={this.scrollToBottom}
                clientData={clientData}
                trainerId={this.props.myTrainer.data[0].id}
                myClient={this.props.myClient}
                myTrainer={this.props.myTrainer}
                {...formikProps}
              />
            )}
          />
        </div>
      </div>
    );
  }
}

const EnhancedComponent = compose(
  withFetch('Client/MyChatRooms', undefined, 'myChatRooms'),
  withFetch('Client/MyClient', undefined, 'myClient'),
  withFetch('Client/MyTrainer', undefined, 'myTrainer'),
  withLoading('myChatRooms'),
  withLoading('myClient'),
  withLoading('myTrainer'),
)(Chat);

EnhancedComponent.displayName = wrapDisplayName(Chat, 'withFetch');

export default EnhancedComponent;
